import React, { useState, useCallback, useMemo, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Paper,
  CircularProgress,
  Divider,
  Stack,
  TextField,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import { useTranslation } from 'react-i18next';
import Table from '../../../../common/components/Table/Table';
import { NotificationSnackbar } from '../../../../common/components/Snackbar';
import axios from 'axios';

interface ASIModalProps {
  isOpen: boolean;
  onClose: () => void;
  row: any;
  dataSetId: number;
  fileDetails?: any;
}

interface AttachmentFile {
  id: string;
  inboundPpo: string;
  outboundPpo: string;
  sampleId: string;
  uploadDate: string;
  uploadedBy: string;
  removalDate: string | null;
  filename: string;
}

const ASIModal: React.FC<ASIModalProps> = ({ isOpen, onClose, row, dataSetId, fileDetails }) => {
  const { t } = useTranslation();
  const [attachments, setAttachments] = useState<AttachmentFile[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    type: 'success' | 'error' | 'info' | 'warning';
  }>({
    open: false,
    message: '',
    type: 'info',
  });

  // Helper function to extract query parameters from row and fileDetails
  const getQueryParams = React.useCallback((): URLSearchParams => {
    const params = new URLSearchParams();
    
    // Use year and quarter directly from fileDetails (preferred)
    // Fall back to calculating from dates if fileDetails doesn't have them
    let year: number;
    let quarter: number;
    
    if (fileDetails?.accountingYear) {
      year = fileDetails.accountingYear;
    } else {
      // Try to extract from row dates
      let date: Date | null = null;
      
      if (row?.samplingDate) {
        date = new Date(row.samplingDate);
        if (isNaN(date.getTime())) date = null;
      }
      
      if (!date && row?.despatchDate) {
        date = new Date(row.despatchDate);
        if (isNaN(date.getTime())) date = null;
      }
      
      // Fallback to current date if all else fails
      if (!date) {
        date = new Date();
      }
      
      year = date.getFullYear();
    }
    
    if (fileDetails?.accountingQuarter) {
      quarter = fileDetails.accountingQuarter;
    } else {
      // Try to extract from row dates
      let date: Date | null = null;
      
      if (row?.samplingDate) {
        date = new Date(row.samplingDate);
        if (isNaN(date.getTime())) date = null;
      }
      
      if (!date && row?.despatchDate) {
        date = new Date(row.despatchDate);
        if (isNaN(date.getTime())) date = null;
      }
      
      // Fallback to current date if all else fails
      if (!date) {
        date = new Date();
      }
      
      quarter = Math.floor(date.getMonth() / 3) + 1;
    }
    
    // Set year and quarter (validate to ensure they're not NaN)
    if (isNaN(year) || year < 1900 || year > 2100) {
      console.warn('Invalid year extracted:', year);
      year = new Date().getFullYear();
    }
    
    if (isNaN(quarter) || quarter < 1 || quarter > 4) {
      console.warn('Invalid quarter extracted:', quarter);
      quarter = Math.floor(new Date().getMonth() / 3) + 1;
    }
    
    params.set('year', year.toString());
    params.set('quarter', quarter.toString());
    
    // Extract operator codes - use fileDetails as first priority
    const inboundOe = fileDetails?.operatorByInboundPpo || row?.oeToCode || row?.receivingPpo || '';
    const outboundOe = fileDetails?.operatorByOutboundPpo || row?.oeFromCode || row?.sendingPpo || '';
    
    params.set('inboundOe', inboundOe);
    params.set('outboundOe', outboundOe);
    params.set('type', 'eno1');
    
    return params;
  }, [row, fileDetails]);

  // Fetch attachments from API
  const fetchAttachments = React.useCallback(async () => {
    setIsLoading(true);
    try {
      // Get rowId from row data - use sampleId first as it's the identifier for attachments
      const rowId = row?.sampleId || row?.sampleDbId || row?.id;
      
      if (!rowId) {
        setNotification({
          open: true,
          message: t('asi.modal.noRowId', 'Sample ID is required'),
          type: 'error',
        });
        setAttachments([]);
        setIsLoading(false);
        return;
      }

      // Get query parameters
      const queryParams = getQueryParams();
      
      // API endpoint: /attachments/list/{rowId}?type=ENO1&year=2024&quarter=1&inboundOe=XX&outboundOe=YY
      const response = await axios.get(`/attachments/list/${rowId}?${queryParams.toString()}`);
      
      setAttachments(response.data);
    } catch (error) {
      console.error('Error fetching attachments:', error);
      setNotification({
        open: true,
        message: t('asi.modal.fetchError', 'Failed to load attachments'),
        type: 'error',
      });
      // Set empty array as fallback
      setAttachments([]);
    } finally {
      setIsLoading(false);
    }
  }, [row, fileDetails, t, getQueryParams]);

  // Fetch attachments when modal opens
  React.useEffect(() => {
    if (isOpen && row) {
      fetchAttachments();
    }
  }, [isOpen, row, fetchAttachments]);

  // Handle file selection
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      setSelectedFiles(Array.from(files));
    }
  };

  // Handle file upload
  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      setNotification({
        open: true,
        message: t('asi.modal.noFilesSelected', 'Please select files to upload'),
        type: 'warning',
      });
      return;
    }

    // Get rowId from row data - use sampleId first as it's the identifier for attachments
    const rowId = row?.sampleId || row?.sampleDbId || row?.id;
    
    if (!rowId) {
      setNotification({
        open: true,
        message: t('asi.modal.noRowId', 'Sample ID is required for file upload'),
        type: 'error',
      });
      return;
    }

    setIsUploading(true);

    try {
      // Get query parameters
      const queryParams = getQueryParams();

      for (const file of selectedFiles) {
        const formData = new FormData();
        formData.append('file', file);

        // Upload endpoint: /attachments/upload/{rowId}?type=ENO1&year=2024&quarter=1&inboundOe=XX&outboundOe=YY
        const uploadUrl = `/attachments/upload/${rowId}?${queryParams.toString()}`;

        const response = await axios.post(uploadUrl, formData, {
          // Axios automatically sets Content-Type to multipart/form-data with boundary
          // No need to set it explicitly, as axios handles it correctly
        });

        // Response is automatically parsed by axios
        const result = response.data;
      }

      // Clear selected files after successful upload
      setSelectedFiles([]);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      // Refresh the attachments list
      await fetchAttachments();

      setNotification({
        open: true,
        message: t('asi.modal.uploadSuccess', 'Files uploaded successfully'),
        type: 'success',
      });
    } catch (error) {
      console.error('Upload error:', error);
      setNotification({
        open: true,
        message: t('asi.modal.uploadError', 'Failed to upload files'),
        type: 'error',
      });
    } finally {
      setIsUploading(false);
    }
  };

  // Format date
  const formatDate = (dateString: string | null): string => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString() + ' ' + new Date(dateString).toLocaleTimeString();
  };

  // Handle file download
  const handleDownload = async (attachment: AttachmentFile) => {
    try {
      // Download endpoint: /attachments/download/{attachmentId}
      const downloadUrl = `/attachments/download/${attachment.id}`;
      
      const response = await axios.get(downloadUrl, {
        responseType: 'blob', // Important for file downloads
      });

      // Extract filename from Content-Disposition header
      let fileName: string = attachment.filename || 'download';
      
      const contentDisposition = 
        response.headers['content-disposition'] || 
        response.headers['Content-Disposition'];
      
      if (contentDisposition) {
        // Match the filename in Content-Disposition header
        const filenameMatch = /filename[^;=\n]*=(['"]?)([^"';]+)\1/i.exec(contentDisposition);
        if (filenameMatch && filenameMatch[2]) {
          fileName = decodeURIComponent(filenameMatch[2]);
        }
      }

      // Create download link
      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setNotification({
        open: true,
        message: t('asi.modal.downloadSuccess', 'File downloaded successfully'),
        type: 'success',
      });
    } catch (error) {
      console.error('Download error:', error);
      setNotification({
        open: true,
        message: t('asi.modal.downloadError', 'Failed to download file'),
        type: 'error',
      });
    }
  };

  // Handle file delete
  const handleDelete = async (attachment: AttachmentFile) => {
    try {
      // Delete endpoint: /attachments/delete/{attachmentId}
      const deleteUrl = `/attachments/delete/${attachment.id}`;
      
      await axios.delete(deleteUrl);

      // Update local state to remove the deleted attachment immediately for better UX
      setAttachments((prev) => prev.filter((att) => att.id !== attachment.id));
      
      // Refetch attachments to ensure consistency with server state
      await fetchAttachments();
      
      setNotification({
        open: true,
        message: t('asi.modal.deleteSuccess', 'File deleted successfully'),
        type: 'success',
      });
    } catch (error) {
      console.error('Delete error:', error);
      setNotification({
        open: true,
        message: t('asi.modal.deleteError', 'Failed to delete file'),
        type: 'error',
      });
    }
  };

  // Table columns for attachments
  const columns = useMemo(
    () => [
      {
        Header: t('asi.modal.table.id', 'ID'),
        accessor: 'id',
        width: '60px',
      },
      {
        Header: t('asi.modal.table.inboundPpo', 'Inbound DO'),
        accessor: 'exchangeOfficeByInboundOe',
        width: '150px',
      },
      {
        Header: t('asi.modal.table.outboundPpo', 'Outbound DO'),
        accessor: 'exchangeOfficeByOutboundOe',
        width: '150px',
      },
      {
        Header: t('asi.modal.table.uploadDate', 'Upload Date'),
        accessor: 'uploadDate',
        width: '150px',
        Cell: ({ value }: { value: string }) => formatDate(value),
      },
      {
        Header: t('asi.modal.table.uploadedBy', 'Uploaded By'),
        accessor: 'uploader',
        width: '150px',
      },
      {
        Header: t('asi.modal.table.removalDate', 'Removal Date'),
        accessor: 'removalDate',
        width: '150px',
        Cell: ({ value }: { value: string | null }) => formatDate(value),
      },
      {
        Header: t('asi.modal.table.filename', 'Filename'),
        accessor: 'fileName',
        width: '400px',
      },
      {
        Header: t('asi.modal.table.actions', 'Actions'),
        accessor: 'id', // Use 'id' as accessor since we need the row data
        width: '100px',
        Cell: ({ value }: { value: string }) => {
          // Find the attachment by id to get full row data
          const attachment = attachments.find(att => att.id === value);
          if (!attachment) return null;
          
          return (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <IconButton
                size="small"
                onClick={() => handleDownload(attachment)}
                title={t('asi.modal.download', 'Download')}
              >
                <DownloadIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => handleDelete(attachment)}
                title={t('asi.modal.delete', 'Delete')}
                color="error"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          );
        },
      },
    ],
    [t, attachments]
  );

  // Handle notification close
  const handleNotificationClose = () => {
    setNotification((prev) => ({ ...prev, open: false }));
  };

  return (
    <>
      <Dialog
        open={isOpen}
        onClose={onClose}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            minHeight: '600px',
            maxHeight: '90vh',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pb: 1,
          }}
        >
          <Typography variant="h6">
            {t('asi.modal.title', 'Additional Sample Information (ASI)')}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          {/* File Upload Section */}
          <Box sx={{ mb: 3 }}>
            <Paper elevation={1} sx={{ p: 3 }}>
              <Stack spacing={2}>
                {/* File Input */}
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept=".pdf,.xlsx,.xls,.csv,.png,.jpg,.jpeg,.gif,.txt"
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                />
                
                {/* File Selection Display */}
                <Box>
                  <TextField
                    fullWidth
                    variant="outlined"
                    label={t('asi.modal.selectedFiles', 'Selected Files')}
                    value={selectedFiles.length > 0 ? selectedFiles.map(f => f.name).join(', ') : ''}
                    placeholder={t('asi.modal.noFilesSelected', 'No files selected')}
                    InputProps={{
                      readOnly: true,
                    }}
                    size="small"
                  />
                </Box>

                {/* Action Buttons */}
                <Stack direction="row" spacing={2}>
                  <Button
                    variant="outlined"
                    startIcon={<FolderOpenIcon />}
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isUploading}
                  >
                    {t('asi.modal.chooseFiles', 'Choose Files')}
                  </Button>
                  
                  <Button
                    variant="contained"
                    startIcon={isUploading ? <CircularProgress size={16} /> : <UploadFileIcon />}
                    onClick={handleUpload}
                    disabled={isUploading || selectedFiles.length === 0}
                  >
                    {isUploading 
                      ? t('asi.modal.uploading', 'Uploading...') 
                      : t('asi.modal.upload', 'Upload')
                    }
                  </Button>
                </Stack>

                {/* File Info */}
                <Typography variant="caption" color="text.secondary">
                  {t('asi.modal.supportedFormats', 'Supported: PDF, Excel, CSV, Images, Text (Max 10MB per file)')}
                </Typography>
              </Stack>
            </Paper>
          </Box>

          {/* Attachments Table */}
          <Box>
            {isLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : attachments.length > 0 ? (
              <Table
                data={attachments}
                columns={columns}
                moduleName={t('asi.modal.attachmentsTable', 'Sample Attachments')}
                pageSize={10}
                page={0}
                viewMode={true}
                isRowClickable={false}
                disableAutoSizing={true}
                totalCount={attachments.length}
              />
            ) : (
              <Paper sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  {t('asi.modal.noAttachments', 'No attachments found for this sample')}
                </Typography>
              </Paper>
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button onClick={onClose} variant="outlined">
            {t('asi.modal.close', 'Close')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notification Snackbar */}
      <NotificationSnackbar
        open={notification.open}
        message={notification.message}
        severity={notification.type}
        onClose={handleNotificationClose}
        data-testid="asi-notification"
      />
    </>
  );
};

export default ASIModal; 